// taxCalculations.js
import taxData from "./taxData2025.json";

// Status mapping for different casing
const statusMap = {
  single: { lower: "single", capital: "Single" },
  marriedJointly: { lower: "marriedJointly", capital: "MarriedFilingJointly" },
  marriedSeparately: {
    lower: "marriedSeparately",
    capital: "MarriedFilingSeparately",
  },
  headOfHousehold: { lower: "headOfHousehold", capital: "HeadOfHousehold" },
  qualifyingSurvivingSpouse: {
    lower: "qualifyingSurvivingSpouse",
    capital: "QualifyingSurvivingSpouse",
  },
};

// Normalize filing status
function normaliseStatus(status, capital = false) {
  const key = status.trim().toLowerCase().replace(/\s+/g, "");
  const baseMap = {
    single: "single",
    marriedfilingjointly: "marriedJointly",
    marriedfilingseparately: "marriedSeparately",
    headofhousehold: "headOfHousehold",
    qualifyingsurvivingspouse: "qualifyingSurvivingSpouse",
    marriedfilingjointly: "marriedJointly", // duplicate for safety
  };
  const base = baseMap[key] || "single";
  return capital ? statusMap[base].capital : statusMap[base].lower;
}

// Apply progressive tax brackets
function applyProgressiveBrackets(income, brackets) {
  if (income <= 0) return { tax: 0, marginalRate: 0 };
  let tax = 0;
  let prev = 0;
  let marginalRate = 0;
  for (const bracket of brackets) {
    const upper =
      bracket.to === "unlimited" || bracket.max === null
        ? Infinity
        : bracket.to || bracket.max;
    const amount = Math.min(income - prev, upper - prev);
    if (amount > 0) {
      tax += amount * bracket.rate;
      marginalRate = bracket.rate;
    }
    prev = upper;
    if (income <= upper) break;
  }
  return { tax, marginalRate };
}

// Apply LTCG brackets, stacking on ordinary income
function applyLTCGBrackets(ordinaryIncome, ltcgAmount, filingStatus) {
  if (ltcgAmount <= 0) return { ltcgTax: 0, marginalLtcgRate: 0 };
  const statusKey = normaliseStatus(filingStatus, true); // Capitalized for LTCG
  const brackets = taxData.federal.ltcgBrackets[statusKey] || [];
  let ltcgTax = 0;
  let marginalLtcgRate = 0;
  let remaining = ltcgAmount;
  let effectiveThreshold = 0;
  for (const bracket of brackets) {
    const bracketUpper = bracket.to === "unlimited" ? Infinity : bracket.to;
    const bracketSize = bracketUpper - effectiveThreshold;
    if (remaining > 0) {
      const taxableInBracket = Math.min(
        remaining,
        Math.max(
          0,
          bracketSize - Math.max(0, ordinaryIncome - effectiveThreshold)
        )
      );
      ltcgTax += taxableInBracket * bracket.rate;
      remaining -= taxableInBracket;
      if (taxableInBracket > 0) marginalLtcgRate = bracket.rate;
    }
    effectiveThreshold = bracketUpper;
  }
  return { ltcgTax, marginalLtcgRate };
}

// Calculate capital gains netting
function calculateCapitalGains({ st = 0, lt = 0, carryforward = 0 }) {
  let netST = st;
  let netLT = lt;

  // Apply carryforward (assumed general loss)
  if (carryforward < 0) {
    const absCarry = Math.abs(carryforward);
    if (netLT > 0) {
      const usable = Math.min(netLT, absCarry);
      netLT -= usable;
      carryforward += usable;
    }
    if (netST > 0 && carryforward < 0) {
      const usable = Math.min(netST, Math.abs(carryforward));
      netST -= usable;
      carryforward += usable;
    }
  }

  // Net ST and LT if opposite signs
  let netSTCG = 0;
  let netLTCG = 0;
  if (netST > 0 && netLT < 0) {
    const net = netST + netLT;
    if (net > 0) netSTCG = net;
    else netLTCG = net;
  } else if (netST < 0 && netLT > 0) {
    const net = netST + netLT;
    if (net > 0) netLTCG = net;
    else netSTCG = net;
  } else {
    netSTCG = netST;
    netLTCG = netLT;
  }

  // Calculate total net loss and deduction
  let totalNetLoss =
    Math.abs(Math.min(netSTCG, 0)) +
    Math.abs(Math.min(netLTCG, 0)) +
    Math.abs(carryforward);
  const capitalLossDeduction = Math.min(
    taxData.federal.capitalLossDeductionLimit,
    totalNetLoss
  );

  // If deduction, set gains to 0
  if (capitalLossDeduction > 0) {
    netSTCG = 0;
    netLTCG = 0;
  }

  return {
    netSTCG: Math.max(0, netSTCG),
    netLTCG: Math.max(0, netLTCG),
    capitalLossDeduction,
  };
}

// Calculate payroll taxes and SE deduction
function calculatePayrollTaxes({
  w2Income = 0,
  seNetIncome = 0,
  filingStatus,
}) {
  const payroll = taxData.federal.payroll;
  const ssWageBase = payroll.socialSecurity.wageBase;

  // W2 FICA
  const w2SS = Math.min(w2Income, ssWageBase) * payroll.socialSecurity.rate;
  const w2Medicare = w2Income * payroll.medicare.rate;

  // SE Tax
  const seTaxableBase = seNetIncome * payroll.seDeduction;
  const remainingSSBase = Math.max(0, ssWageBase - w2Income);
  const seSS =
    Math.min(seTaxableBase, remainingSSBase) * payroll.socialSecurity.seRate;
  const seMedicare = seTaxableBase * payroll.medicare.seRate;
  const seTaxDeduction = (seSS + seMedicare) / 2;

  // Additional Medicare
  const totalWages = w2Income + seNetIncome;
  let medicareThreshold = payroll.medicare.additionalThreshold;
  if (filingStatus.toLowerCase().includes("jointly"))
    medicareThreshold = 250000;
  else if (filingStatus.toLowerCase().includes("separately"))
    medicareThreshold = 125000;
  const additionalMedicareTax =
    Math.max(0, totalWages - medicareThreshold) *
    payroll.medicare.additionalRate;

  const totalPayrollTax =
    w2SS + w2Medicare + seSS + seMedicare + additionalMedicareTax;

  return { totalPayrollTax, seTaxDeduction };
}

// Calculate AGI with fixes
function calculateAGI({
  grossIncome,
  filingStatus,
  aboveTheLine,
  seTaxDeduction,
  capitalLossDeduction,
  netSTCG,
  netLTCG,
}) {
  let agi = grossIncome - seTaxDeduction;

  // Student Loan Interest (phaseout on current AGI)
  const studentLoanLimit = 2500;
  let studentLoanDeduction = Math.min(
    aboveTheLine.studentLoan || 0,
    studentLoanLimit
  );
  const agiForPhaseout = agi;
  const isJoint = filingStatus.toLowerCase().includes("jointly");
  const phaseoutStart = isJoint ? 155000 : 75000;
  const phaseoutRange = isJoint ? 30000 : 15000;
  if (agiForPhaseout > phaseoutStart) {
    const phaseoutPercent = Math.min(
      1,
      (agiForPhaseout - phaseoutStart) / phaseoutRange
    );
    studentLoanDeduction *= 1 - phaseoutPercent;
  }
  agi -= studentLoanDeduction;

  // Other deductions
  agi -= Math.max(0, aboveTheLine.hsa || 0);
  agi -= Math.max(0, aboveTheLine.tradIra || 0);
  agi -= Math.min(aboveTheLine.educator || 0, 300);

  // Capital loss deduction
  agi -= capitalLossDeduction;

  // Add net capital gains (fix for AGI including gains)
  agi += netSTCG + netLTCG;

  return Math.max(0, agi);
}

// Calculate dependent care credit
function calculateDependentCareCredit({ AGI, dependents, credits }, taxLimit) {
  const rules = taxData.federal.credits.dependentCare;
  if (
    credits.dependentCareExpenses <= 0 ||
    dependents <= 0 ||
    AGI > rules.finalPhaseout
  )
    return 0;
  const expenseLimit =
    dependents === 1 ? rules.maxExpenses["1"] : rules.maxExpenses["2+"];
  const expenses = Math.min(credits.dependentCareExpenses, expenseLimit);
  let rate = rules.baseRate;
  if (AGI > rules.phaseoutStart) {
    const reduction = Math.floor((AGI - rules.phaseoutStart) / 2000) * 0.01;
    rate = Math.max(rules.minRate, rules.baseRate - reduction);
  }
  const credit = expenses * rate;
  return Math.min(taxLimit, credit);
}

// Calculate education credit (AOTC)
function calculateEducationCredit({ AGI, filingStatus, credits }) {
  const rules = taxData.federal.credits.education;
  if (credits.educationExpenses <= 0)
    return { nonRefundableAmount: 0, refundableAmount: 0 };
  const phaseoutStart = rules.phaseoutStart[filingStatus];
  const phaseoutEnd = rules.phaseoutEnd[filingStatus];
  if (AGI >= phaseoutEnd)
    return { nonRefundableAmount: 0, refundableAmount: 0 };
  const expenses = credits.educationExpenses;
  let credit =
    Math.min(expenses, 2000) +
    Math.max(0, Math.min(expenses - 2000, 2000)) * 0.25;
  credit = Math.min(credit, rules.maxCredit);
  if (AGI > phaseoutStart) {
    const phaseoutPercent =
      (AGI - phaseoutStart) / (phaseoutEnd - phaseoutStart);
    credit *= 1 - phaseoutPercent;
  }
  return {
    nonRefundableAmount: credit * rules.nonRefundablePortion,
    refundableAmount: credit * rules.refundablePortion,
  };
}

// Calculate saver's credit
function calculateSaversCredit({ AGI, filingStatus, aboveTheLine }, taxLimit) {
  const contribution = (aboveTheLine && aboveTheLine.tradIra) || 0;
  if (contribution <= 0) return 0;
  const rules = taxData.federal.credits.savers;
  const tiers = rules.rates[filingStatus] || rules.rates["Single"];
  let rate = 0;
  for (const tier of tiers) {
    if (AGI <= (tier.maxAGI || Infinity)) {
      rate = tier.rate;
      break;
    }
  }
  if (rate === 0) return 0;
  const credit = Math.min(contribution, rules.maxContribution) * rate;
  return Math.min(taxLimit, credit);
}

// Calculate EITC
function calculateEITC({
  AGI,
  earnedIncome,
  dependents,
  filingStatus,
  netShortTermCG,
  netLongTermCG,
}) {
  const rules = taxData.federal.credits.eitc;
  const investmentIncome = Math.max(0, netShortTermCG + netLongTermCG);
  if (investmentIncome > rules.investmentIncomeLimit) return 0;
  const numKids = Math.min(dependents, 3);
  const normalizedStatus = normaliseStatus(filingStatus, false);
  const fallbackMap = {
    marriedJointly: "married",
    marriedSeparately: "married",
    qualifyingSurvivingSpouse: "married",
  };

  const statusKeyNew = fallbackMap[normalizedStatus];

  const statusKey = filingStatus.includes("Jointly")
    ? "MarriedFilingJointly"
    : "Single";
  const tier = rules && rules[normalizedStatus][numKids];
  if (!tier) return 0;
  let credit = 0;
  if (earnedIncome <= tier.phaseinEnd) {
    credit = earnedIncome * (tier.maxCredit / tier.phaseinEnd);
  } else if (earnedIncome <= tier.phaseoutStart) {
    credit = tier.maxCredit;
  } else if (earnedIncome < tier.phaseoutEnd) {
    credit =
      tier.maxCredit -
      (earnedIncome - tier.phaseoutStart) *
        (tier.maxCredit / (tier.phaseoutEnd - tier.phaseoutStart));
  } else {
    return 0;
  }
  if (AGI > tier.phaseoutStart) {
    const agiBasedCredit =
      tier.maxCredit -
      (AGI - tier.phaseoutStart) *
        (tier.maxCredit / (tier.phaseoutEnd - tier.phaseoutStart));
    credit = Math.min(credit, agiBasedCredit);
  }
  if (AGI >= tier.phaseoutEnd) return 0;
  return Math.max(0, credit);
}

// Calculate federal tax
function calculateFederalTax(inputs, capGainsResult) {
  const { AGI, filingStatus, deductionType, itemized, dependents, credits } =
    inputs;
  const { netSTCG, netLTCG } = capGainsResult;
  const normalizedStatus = normaliseStatus(filingStatus, false);
  const fallbackMap = {
    marriedJointly: "married",
    marriedSeparately: "married",
    qualifyingSurvivingSpouse: "married",
  };
  const statusKey = fallbackMap[normalizedStatus];

  const standardDeduction = taxData.federal.standardDeduction[statusKey] || 0;
  let deduction = standardDeduction;
  let deductionTypeUsed = `Standard (${standardDeduction})`;
  if (deductionType === "itemized") {
    const medicalFloor = AGI * 0.075;
    const deductibleMedical = Math.max(
      0,
      (itemized.medical || 0) - medicalFloor
    );
    const cappedSalt = Math.min(itemized.salt || 0, 10000);
    const itemizedTotal =
      deductibleMedical +
      cappedSalt +
      (itemized.mortgage || 0) +
      (itemized.charity || 0);
    if (itemizedTotal > standardDeduction) {
      deduction = itemizedTotal;
      deductionTypeUsed = `Itemized (${itemizedTotal})`;
    }
  }

  const ordinaryTaxableIncome = Math.max(0, AGI - deduction - netLTCG);
  const brackets = taxData.federal.brackets[normalizedStatus] || [];
  const { tax: ordinaryTax, marginalRate: ordinaryMarginalRate } =
    applyProgressiveBrackets(ordinaryTaxableIncome, brackets);
  const { ltcgTax, marginalLtcgRate } = applyLTCGBrackets(
    ordinaryTaxableIncome,
    netLTCG,
    filingStatus
  );

  let taxPreCredit = ordinaryTax + ltcgTax;
  let remainingTax = taxPreCredit;

  // Non-refundable credits
  const creditDepCare = calculateDependentCareCredit(inputs, remainingTax);
  // console.log("deductions", taxPreCredit, remainingTax, creditDepCare);
  remainingTax = Math.max(0, remainingTax - creditDepCare);
  const educationCredits = calculateEducationCredit(inputs);
  const nonRefundableAOTC = Math.min(
    remainingTax,
    educationCredits.nonRefundableAmount
  );
  remainingTax = Math.max(0, remainingTax - nonRefundableAOTC);
  const creditSavers = calculateSaversCredit(inputs, remainingTax);
  remainingTax = Math.max(0, remainingTax - creditSavers);
  const totalNonRefundableCredits =
    creditDepCare + nonRefundableAOTC + creditSavers;

  // Child Tax Credit
  const ctcRules = taxData.federal.credits.childTaxCredit;
  const phaseoutThreshold = ctcRules.phaseoutThreshold[normalizedStatus];
  let ctcAmount = 0;
  if (AGI < phaseoutThreshold) {
    ctcAmount = dependents * ctcRules.amountPerChild;
  } else {
    const phaseoutAmount = Math.ceil((AGI - phaseoutThreshold) / 1000) * 1000;
    const reduction = phaseoutAmount * ctcRules.phaseoutRate;
    ctcAmount = Math.max(0, dependents * ctcRules.amountPerChild - reduction);
  }
  const nonRefundableCTC = Math.min(remainingTax, ctcAmount);
  remainingTax = Math.max(0, remainingTax - nonRefundableCTC);
  const potentialRefundableCTC = ctcAmount - nonRefundableCTC;
  const maxRefundableCTC = dependents * ctcRules.refundableAmountPerChild;
  const actualRefundableCTC = Math.min(
    potentialRefundableCTC,
    maxRefundableCTC
  );

  // Refundable credits
  remainingTax -= actualRefundableCTC;
  const creditEITC = calculateEITC({
    ...inputs,
    earnedIncome: inputs.grossIncome,
    netShortTermCG: capGainsResult.netSTCG,
    netLongTermCG: capGainsResult.netLTCG,
  });
  remainingTax -= creditEITC;
  const totalRefundableCredits =
    creditEITC + actualRefundableCTC + educationCredits.refundableAmount;

  const totalFederalTax = remainingTax;
  return {
    totalFederalTax,
    ordinaryTaxableIncome,
    ordinaryTax,
    ltcgTax,
    totalNonRefundableCredits,
    ctcAmount: nonRefundableCTC,
    totalRefundableCredits,
    deductionType: deductionTypeUsed,
    marginalRates: { ordinary: ordinaryMarginalRate, ltcg: marginalLtcgRate },
  };
}

// Calculate state and local tax
function calculateStateAndLocalTax(inputs, federalResult) {
  const { AGI, filingStatus, state: stateKey, city, dependents } = inputs;
  let stateObj = taxData.states.find(
    (s) => s.state && s.state.toLowerCase() === stateKey.toLowerCase()
  );
  if (!stateObj && stateKey.toLowerCase() === "illinois")
    stateObj = taxData.illinois;
  if (!stateObj)
    return {
      stateName: stateKey,
      cityName: "N/A",
      stateTax: 0,
      localTax: 0,
      taxableIncome: 0,
    };

  const statusKey = normaliseStatus(filingStatus, false);
  const fallbackMap = {
    marriedJointly: "married",
    marriedSeparately: "married",
    qualifyingSurvivingSpouse: "married",
  };
  const fallbackKey = fallbackMap[statusKey];
  let result = {
    stateName: stateObj.state || stateObj.name || stateKey,
    cityName: "N/A",
    stateTax: 0,
    localTax: 0,
    taxableIncome: 0,
  };

  if (stateObj.noIncomeTax) return result;

  let deduction = stateObj.standardDeduction
    ? stateObj.standardDeduction[fallbackKey] || 0
    : 0;

  let taxableIncome = Math.max(0, AGI - deduction);
  result.taxableIncome = taxableIncome;

  if (stateKey.toLowerCase() === "illinois") {
    const numExemptions = filingStatus.includes("Jointly")
      ? 2 + dependents
      : 1 + dependents;
    let exemptionAmount = stateObj.personalExemption * numExemptions;
    const phaseoutLimit = stateObj.exemptionPhaseout[filingStatus];
    if (AGI > phaseoutLimit) exemptionAmount = 0;
    taxableIncome = Math.max(0, AGI - exemptionAmount);
    result.taxableIncome = taxableIncome;
    result.stateTax = taxableIncome * stateObj.flatTaxRate;
  } else if (stateKey.toLowerCase() === "california") {
    const brackets = stateObj.brackets[statusKey] || [];
    // console.log("bracketys", brackets, result);
    const { tax } = applyProgressiveBrackets(taxableIncome, brackets);
    result.stateTax = tax;
  } else if (stateKey.toLowerCase() === "new york") {
    const brackets = stateObj.brackets[statusKey] || [];
    const { tax: stateTax } = applyProgressiveBrackets(taxableIncome, brackets);
    result.stateTax = stateTax;
    if (city.toLowerCase() === "new_york_city") {
      const cityData = taxData.cities.find((c) => c.name === "New York City");
      if (cityData) {
        result.cityName = cityData.name;
        const cityStatusKey = normaliseStatus(filingStatus, true); // Capitalized for NYC
        const cityBrackets = cityData.taxBrackets[cityStatusKey] || [];
        const { tax: cityTax } = applyProgressiveBrackets(
          taxableIncome,
          cityBrackets
        );
        result.localTax = cityTax;
      }
    }
  }
  return result;
}

// Main calculation function
export function calculateTotalTax({
  filingStatus = "MarriedFilingJointly",
  dependents = 0,
  state = "Illinois",
  city = "Other",
  deductionType = "standard",
  w2Income = 0,
  seIncome = 0,
  hsa = 0,
  studentLoan = 0,
  tradIra = 0,
  educator = 0,
  netShortTermCG = 0,
  netLongTermCG = 0,
  carryforward = 0,
  dependentCareExp = 0,
  educationExp = 0,
  itemizedMedical = 0,
  itemizedSalt = 0,
  itemizedMortgage = 0,
  itemizedCharity = 0,
  isMarginalCalc = false,
}) {
  const grossIncome = w2Income + seIncome;
  const capGainsInputs = {
    st: netShortTermCG,
    lt: netLongTermCG,
    carryforward,
  };
  const capGainsResult = calculateCapitalGains(capGainsInputs);
  const payrollResult = calculatePayrollTaxes({
    w2Income,
    seNetIncome: seIncome,
    filingStatus,
  });
  const aboveTheLine = { hsa, studentLoan, tradIra, educator };
  const AGI = calculateAGI({
    grossIncome,
    filingStatus,
    aboveTheLine,
    seTaxDeduction: payrollResult.seTaxDeduction,
    capitalLossDeduction: capGainsResult.capitalLossDeduction,
    netSTCG: capGainsResult.netSTCG,
    netLTCG: capGainsResult.netLTCG,
  });
  const inputs = {
    AGI,
    filingStatus,
    deductionType,
    dependents,
    state,
    city,
    grossIncome,
    itemized: {
      medical: itemizedMedical,
      salt: itemizedSalt,
      mortgage: itemizedMortgage,
      charity: itemizedCharity,
    },
    credits: {
      dependentCareExpenses: dependentCareExp,
      educationExpenses: educationExp,
    },
  };
  const federalResult = calculateFederalTax(inputs, capGainsResult);
  const stateResult = calculateStateAndLocalTax(inputs, federalResult);
  // const totalTax =
  //   federalResult.totalFederalTax +
  //   stateResult.stateTax +
  //   stateResult.localTax +
  //   payrollResult.totalPayrollTax;

  // === DEDUCTIONS ===
  const statusKey = normaliseStatus(filingStatus, false);
  const fallbackMap = {
    marriedJointly: "married",
    marriedSeparately: "married",
    qualifyingSurvivingSpouse: "married",
  };
  const federalDeduction =
    deductionType === "standard"
      ? taxData.federal.standardDeduction[
          fallbackMap[statusKey] || statusKey
        ] || 0
      : Math.max(
          0,
          AGI - federalResult.ordinaryTaxableIncome - capGainsResult.netLTCG
        );

  let stateDeduction = 0;
  if (stateResult.stateName && !stateResult.noIncomeTax) {
    const stateObj = taxData.states.find(
      (s) => s.state.toLowerCase() === state.toLowerCase()
    );
    if (stateObj) {
      if (state.toLowerCase() === "illinois") {
        const numExemptions = filingStatus.includes("Jointly")
          ? 2 + dependents
          : 1 + dependents;
        stateDeduction = stateObj.personalExemption * numExemptions;
      } else {
        stateDeduction =
          (stateObj.standardDeduction &&
            stateObj.standardDeduction[fallbackMap[statusKey] || statusKey]) ||
          0;
      }
    }
  }

  const totalTax =
    federalResult.totalFederalTax +
    stateResult.stateTax +
    stateResult.localTax +
    payrollResult.totalPayrollTax;

  const totalTaxLiability = totalTax;

  const effectiveRate = grossIncome
    ? ((totalTax / grossIncome) * 100).toFixed(2)
    : "0.00";

  let marginalRate = "0.00";
  if (!isMarginalCalc) {
    const plusOne = calculateTotalTax({
      ...arguments[0],
      w2Income: w2Income + 1,
      isMarginalCalc: true,
    });
    marginalRate = ((plusOne.totalTax - totalTax) * 100).toFixed(2);
  }

  return {
    grossIncome,
    capLossDeduction: capGainsResult.capitalLossDeduction,
    AGI,
    federalDeduction, // NEW
    stateDeduction,
    deductionType: federalResult.deductionType,
    ordTaxable: federalResult.ordinaryTaxableIncome,
    ltcgTaxable: capGainsResult.netLTCG,
    fedOrdTax: federalResult.ordinaryTax,
    fedLtcgTax: federalResult.ltcgTax,
    nonRefundableCredits: federalResult.totalNonRefundableCredits,
    ctc: federalResult.ctcAmount,
    refundableCredits: federalResult.totalRefundableCredits,
    fedTax: federalResult.totalFederalTax,
    stateName: stateResult.stateName,
    stateTaxable: stateResult.taxableIncome,
    stateTax: stateResult.stateTax,
    cityName: stateResult.cityName,
    localTax: stateResult.localTax,
    payrollTax: payrollResult.totalPayrollTax,
    totalTaxLiability, // NEW
    totalTax,
    effectiveRate,
    marginalRate,
  };
}

// Additional functions (kept from old version, updated)
export function calculateCapitalGainsTax({
  shortTermGains = 0,
  shortTermLosses = 0,
  longTermGains = 0,
  longTermLosses = 0,
  carryforwardLoss = 0,
  ordinaryIncome = 0,
  filingStatus = "single",
  stateName = "California",
}) {
  const netShort = shortTermGains - shortTermLosses;
  const netLong = longTermGains - longTermLosses;
  const capGainsResult = calculateCapitalGains({
    st: netShort,
    lt: netLong,
    carryforward: carryforwardLoss,
  });
  const statusKey = normaliseStatus(filingStatus, true);
  const ltcgBrackets = taxData.federal.ltcgBrackets[statusKey] || [];
  const fedTax = applyProgressiveBrackets(
    capGainsResult.netLTCG,
    ltcgBrackets
  ).tax;

  let stateTax = 0;
  const stateObj = taxData.states.find(
    (s) => s.state && s.state.toLowerCase() === stateName.toLowerCase()
  );
  if (stateObj && stateObj.brackets) {
    const stateStatusKey = normaliseStatus(filingStatus, false);
    const stateBrackets = stateObj.brackets[stateStatusKey] || [];
    const stateMarginal = applyProgressiveBrackets(
      ordinaryIncome,
      stateBrackets
    ).marginalRate;
    stateTax =
      (capGainsResult.netSTCG + capGainsResult.netLTCG) * stateMarginal;
  }

  return {
    netCapitalGain: capGainsResult.netSTCG + capGainsResult.netLTCG,
    netCapitalLoss: Math.max(
      0,
      Math.abs(netShort) +
        Math.abs(netLong) -
        shortTermGains -
        longTermGains +
        Math.abs(carryforwardLoss)
    ),
    deductibleLoss: capGainsResult.capitalLossDeduction,
    carryforwardLoss: Math.max(
      0,
      Math.abs(netShort) +
        Math.abs(netLong) -
        shortTermGains -
        longTermGains +
        Math.abs(carryforwardLoss) -
        capGainsResult.capitalLossDeduction
    ),
    capitalGainsTax: fedTax + stateTax,
  };
}

export function calculateProjectionSeries(params) {
  const { inflation = 0.03, years = 10 } = params;
  const baseYear = new Date().getFullYear();
  const projections = [];
  let curW2 = params.w2Income || 0;
  let curSE = params.seIncome || 0;
  for (let i = 0; i < years; i++) {
    const res = calculateTotalTax({
      ...params,
      w2Income: curW2,
      seIncome: curSE,
    });
    const fedDed = res.federalDeduction || 0;
    const stateDed = res.stateDeduction || 0;
    const inflationFactor = Math.pow(1 + inflation, i);
    projections.push({
      year: baseYear + i,
      income: res.grossIncome,
      federal: {
        standardDeduction: Math.round(fedDed * inflationFactor),
        taxableIncome: Math.round(res.ordTaxable + res.ltcgTaxable),
        tax: Math.round(res.fedTax),
      },
      state: res.stateName
        ? {
            state: res.stateName,
            standardDeduction: Math.round(stateDed * inflationFactor),
            taxableIncome: Math.round(res.stateTaxable),
            tax: Math.round(res.stateTax),
            noTax: false,
          }
        : {
            state: "N/A",
            standardDeduction: 0,
            taxableIncome: 0,
            tax: 0,
            noTax: true,
          },
      credits: {
        refundable: res.refundableCredits,
        nonRefundable: res.nonRefundableCredits,
      },
      federalTax: res.fedTax,
      stateTax: res.stateTax,
      totalTax: res.totalTax,
      totalTaxLiability: res.totalTaxLiability,
      effectiveRate: res.effectiveRate,
      marginalRate: res.marginalRate,
    });
    curW2 *= 1 + inflation;
    curSE *= 1 + inflation;
  }
  const chartData = {
    labels: projections.map(p => p.year),
    datasets: {
      federal: projections.map(p => p.federal.tax),
      state: projections.map(p => p.state.tax),
      total: projections.map(p => p.totalTax),
    },
  };
  return { baseYear, projections,chartData };
}
